import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../lib/api'
import { formatMoney } from '../lib/money'
import type { ContractResponse } from '../lib/types'

/** `formatMoney` bound to the account's contract currency and the active UI
 * language. Until `/api/v1/contract` answers, amounts render in EUR — the
 * symbol every page hardcoded before (MS-819 review). Whole amounts print
 * with no decimals and fractional ones with Intl's default for the currency,
 * unless the caller fixes `fractionDigits`. */
export function useMoney(): (amount: number, fractionDigits?: number) => string {
  const { i18n } = useTranslation()
  const { data: contract } = useQuery({
    queryKey: ['contract'],
    queryFn: () => apiFetch<ContractResponse>('/api/v1/contract'),
  })
  const currency = contract?.currency ?? 'EUR'
  return useCallback(
    (amount: number, fractionDigits?: number) =>
      formatMoney(amount, currency, {
        locale: i18n.language,
        fractionDigits: fractionDigits ?? (Number.isInteger(amount) ? 0 : undefined),
      }),
    [currency, i18n.language]
  )
}
